import { LitElement, css, svg, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import type { Clocks, PlayerClock } from "../protocol";

// The chess clocks (#201) as the Room last sent them, and when they arrived: the Room sends them
// only when they change, so a running clock counts down from then.
export type ClockReading = { clocks: Clocks; receivedAt: number };

// Under a minute left, a clock shows it.
const LOW_MS = 60_000;

export function playerClock(reading: ClockReading | null, seat: string): PlayerClock | null {
  return reading?.clocks.players.find((p) => p.seat === seat) ?? null;
}

function leftNow(ms: number, running: boolean, since: number, now: number = performance.now()): number {
  return running ? Math.max(0, ms - (now - since)) : ms;
}

function clockText(ms: number): string {
  const seconds = Math.ceil(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

// Each clock element redraws itself while it runs, so the board around it doesn't.
abstract class Ticking extends LitElement {
  @property({ type: Number }) ms = 0;
  @property({ type: Boolean }) running = false;
  // performance.now() when `ms` was read.
  @property({ type: Number }) since = 0;
  private ticker: number | null = null;

  protected get left(): number {
    return leftNow(this.ms, this.running, this.since);
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.ticker = window.setInterval(() => { if (this.running) this.requestUpdate(); }, 250);
  }

  disconnectedCallback(): void {
    if (this.ticker !== null) window.clearInterval(this.ticker);
    this.ticker = null;
    super.disconnectedCallback();
  }

  protected updated(changed: PropertyValues<this>): void {
    super.updated(changed);
    this.toggleAttribute("low", this.left < LOW_MS);
  }
}

// A time left, as m:ss, in the page's own tree, so the page styles it (clock-time[low]) and reads it.
@customElement("clock-time")
export class ClockTime extends Ticking {
  protected createRenderRoot(): HTMLElement {
    return this;
  }

  render(): string {
    return clockText(this.left);
  }
}

// The avatar's edge, clockwise from the top middle; the avatar's corners clip it.
const EDGE = "M50 0 H100 V100 H0 V0 Z";

// The avatar's ring: how much of the player's time is left, orange while it runs.
@customElement("clock-ring")
export class ClockRing extends Ticking {
  @property({ type: Number }) budget = 0;

  static styles = css`
    :host { position: absolute; inset: 0; pointer-events: none; }
    svg { display: block; width: 100%; height: 100%; }
    .ring-bg { stroke: rgb(0 0 0 / 0.45); }
    .ring { stroke: var(--text-faint); transition: stroke 200ms; }
    :host([running]) .ring { stroke: var(--ask); }
    :host([low]) .ring { stroke: var(--bad); }
  `;

  protected updated(changed: PropertyValues<this>): void {
    super.updated(changed);
    this.toggleAttribute("running", this.running);
  }

  render() {
    const share = this.budget > 0 ? Math.min(1, this.left / this.budget) : 1;
    return svg`<svg viewBox="0 0 100 100" preserveAspectRatio="none"><path class="ring-bg" d=${EDGE} fill="none" stroke-width="6" vector-effect="non-scaling-stroke"></path>
      <path class="ring" d=${EDGE} fill="none" stroke-width="6" vector-effect="non-scaling-stroke" pathLength="100" stroke-dasharray="100" stroke-dashoffset=${100 * (1 - share)}></path></svg>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "clock-time": ClockTime;
    "clock-ring": ClockRing;
  }
}
